import type { AccelSample } from './types';

/**
 * Estimate the gravity vector from raw accelerometer samples and decompose
 * incoming acceleration into vertical (aligned with gravity) and lateral
 * (perpendicular to gravity) components — both in the road frame, not the
 * device frame.
 *
 * This is essential: a phone in a cupholder reads accelerometer values in
 * the device's own frame, which is rotated arbitrarily relative to the car.
 * Without re-projection, a pothole jolt could appear on any axis.
 *
 * Implementation: low-pass filter the raw accelerometer to estimate the
 * gravity direction (≈1 Hz cutoff). Anything sharper than that filter
 * represents real motion. Decompose by projecting onto the gravity unit
 * vector and taking the residual.
 *
 * `alpha` is chosen for ~50 Hz input: 0.95 gives a ~3.5 Hz -3dB cutoff —
 * too high, gravity tracks bumps; too low, gravity lags real reorientation
 * (e.g. phone repositioned mid-drive). 0.99 → ~0.7 Hz which is closer to
 * "track only the slow tilt drift" — tunable in M2.
 */
export class GravityEstimator {
  private gx = 0;
  private gy = 0;
  private gz = 9.81;
  private initialised = false;

  constructor(private readonly alpha = 0.99) {}

  feed(s: AccelSample): { aVert: number; aLateral: number; gMag: number } {
    if (!this.initialised) {
      // Seed from the first sample so we don't have a 30s "settle" period
      this.gx = s.ax;
      this.gy = s.ay;
      this.gz = s.az;
      this.initialised = true;
    } else {
      this.gx = this.alpha * this.gx + (1 - this.alpha) * s.ax;
      this.gy = this.alpha * this.gy + (1 - this.alpha) * s.ay;
      this.gz = this.alpha * this.gz + (1 - this.alpha) * s.az;
    }

    const gMag = Math.hypot(this.gx, this.gy, this.gz) || 9.81;
    const gxn = this.gx / gMag;
    const gyn = this.gy / gMag;
    const gzn = this.gz / gMag;

    // Project total accel onto gravity unit vector; subtract gravity itself
    // so a stationary phone reads ~0, not ~g.
    const aDotG = s.ax * gxn + s.ay * gyn + s.az * gzn;
    const aVert = aDotG - gMag;

    // Lateral magnitude = |a - (a·ĝ)ĝ|
    const lx = s.ax - aDotG * gxn;
    const ly = s.ay - aDotG * gyn;
    const lz = s.az - aDotG * gzn;
    const aLateral = Math.hypot(lx, ly, lz);

    return { aVert, aLateral, gMag };
  }
}
